/**
 * Edit Financial Profile Screen
 * Form to edit financial profile information
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useProfileStore } from '../store/profileStore';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { analytics, AnalyticsEvent } from '../utils/analytics';

export default function EditFinancialProfileScreen() {
  const navigation = useNavigation();
  const { profile, hasProfile, updateProfile } = useProfileStore();

  const [formData, setFormData] = useState({
    monthlyIncome: profile?.monthly_income?.toString() || '',
    monthlyExpenses: profile?.monthly_expenses?.toString() || '',
    currentSavings: profile?.current_savings?.toString() || '',
    financialGoals: profile?.financial_goals?.join(', ') || '',
    riskTolerance: profile?.risk_tolerance || 'medium',
  });

  const [loading, setLoading] = useState(false);
  const [showRiskPicker, setShowRiskPicker] = useState(false);

  const riskToleranceOptions = [
    { label: 'Conservateur (Faible risque)', value: 'low' as const },
    { label: 'Modéré (Risque moyen)', value: 'medium' as const },
    { label: 'Agressif (Risque élevé)', value: 'high' as const },
  ];

  // Track diagnostic viewed when screen is opened
  useEffect(() => {
    analytics.trackEvent(AnalyticsEvent.DIAGNOSTIC_VIEWED, {
      has_profile: hasProfile,
    });
  }, [hasProfile]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Convert string values to numbers and split goals into array
      const profileData = {
        monthly_income: parseFloat(formData.monthlyIncome) || 0,
        monthly_expenses: parseFloat(formData.monthlyExpenses) || 0,
        current_savings: parseFloat(formData.currentSavings) || 0,
        financial_goals: formData.financialGoals
          ? formData.financialGoals.split(',').map(g => g.trim()).filter(g => g)
          : [],
        risk_tolerance: formData.riskTolerance,
      };

      await updateProfile(profileData);

      Alert.alert('Succès', 'Votre profil financier a été mis à jour avec succès');
      navigation.goBack();
    } catch (error) {
      Alert.alert('Erreur', 'Une erreur est survenue lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleCancel} style={styles.backButton}>
          <Icon name="arrow-left" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Profil Financier</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView style={styles.content}>
        <View style={styles.form}>
          {/* Financial Data */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Informations Financières</Text>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Revenus mensuels (€)</Text>
              <Input
                value={formData.monthlyIncome}
                onChangeText={(text) => setFormData({ ...formData, monthlyIncome: text })}
                placeholder="3000"
                keyboardType="numeric"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Dépenses mensuelles (€)</Text>
              <Input
                value={formData.monthlyExpenses}
                onChangeText={(text) => setFormData({ ...formData, monthlyExpenses: text })}
                placeholder="2000"
                keyboardType="numeric"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Épargne actuelle (€)</Text>
              <Input
                value={formData.currentSavings}
                onChangeText={(text) => setFormData({ ...formData, currentSavings: text })}
                placeholder="10000"
                keyboardType="numeric"
              />
            </View>
          </View>

          {/* Financial Goals */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Objectifs Financiers</Text>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Vos objectifs</Text>
              <Input
                value={formData.financialGoals}
                onChangeText={(text) => setFormData({ ...formData, financialGoals: text })}
                placeholder="Épargner pour acheter une maison, préparer la retraite..."
                multiline
                numberOfLines={4}
                style={styles.textArea}
              />
              <Text style={styles.hint}>
                Décrivez vos objectifs financiers (amessage immobilier, retraite, épargne, etc.)
              </Text>
            </View>
          </View>

          {/* Investment Profile */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Profil d'Investisseur</Text>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Tolérance au risque</Text>
              <TouchableOpacity
                style={styles.pickerButton}
                onPress={() => setShowRiskPicker(!showRiskPicker)}
              >
                <Text style={styles.pickerButtonText}>
                  {riskToleranceOptions.find(opt => opt.value === formData.riskTolerance)?.label || 'Sélectionner'}
                </Text>
                <Icon name="chevron-down" size={20} color={colors.textSecondary} />
              </TouchableOpacity>

              {showRiskPicker && (
                <View style={styles.pickerOptions}>
                  {riskToleranceOptions.map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={styles.pickerOption}
                      onPress={() => {
                        setFormData({ ...formData, riskTolerance: option.value });
                        setShowRiskPicker(false);
                      }}
                    >
                      <Text style={styles.pickerOptionText}>{option.label}</Text>
                      {formData.riskTolerance === option.value && (
                        <Icon name="check" size={20} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actions}>
          <Button
            title="Enregistrer"
            onPress={handleSave}
            loading={loading}
            disabled={loading}
          />
          <TouchableOpacity onPress={handleCancel} style={styles.cancelButton}>
            <Text style={styles.cancelButtonText}>Annuler</Text>
          </TouchableOpacity>
        </View>

        {/* Reconciliation Link */}
        <TouchableOpacity
          style={styles.reconciliationLink}
          onPress={() => (navigation as any).navigate('Reconciliation')}
          activeOpacity={0.7}
        >
          <Icon name="scale-balance" size={20} color={colors.primary} />
          <View style={styles.reconciliationLinkContent}>
            <Text style={styles.reconciliationLinkTitle}>Réconciliation budget</Text>
            <Text style={styles.reconciliationLinkSubtitle}>
              Compare tes déclarations vs tes dépenses réelles
            </Text>
          </View>
          <Icon name="chevron-right" size={20} color={colors.textSecondary} />
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  backButton: {
    padding: spacing.xs,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text,
  },
  headerRight: {
    width: 40,
  },
  content: {
    flex: 1,
  },
  form: {
    padding: spacing.lg,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.md,
  },
  formGroup: {
    marginBottom: spacing.lg,
  },
  label: {
    fontSize: typography.fontSize.md,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  hint: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  pickerButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
    flex: 1,
  },
  pickerOptions: {
    marginTop: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  pickerOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  pickerOptionText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
  },
  actions: {
    padding: spacing.lg,
    gap: spacing.md,
    paddingBottom: spacing.xxl,
  },
  cancelButton: {
    padding: spacing.md,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  reconciliationLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.xxl,
    padding: spacing.md,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    gap: spacing.sm,
  },
  reconciliationLinkContent: {
    flex: 1,
  },
  reconciliationLinkTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
  },
  reconciliationLinkSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 2,
  },
});
